import { useState } from "react"
import { Check } from "lucide-react"
import BackLink from "@/components/ui/BackLink"
import PaystackPop from '@paystack/inline-js'

import { initializeSubscription, verifySubscription } from "../../services/subscriptionService"
import { toast } from "sonner"
import Button from "../../components/ui/Button"
import { useAuth } from "../../contexts/authContext"
import LoginPromptModal from "../../components/modals/LoginPromptModal"

export default function SubscriptionPage() {
  const [billingCycle, setBillingCycle] = useState("monthly")
  let [loading, setLoading] = useState(false)
  let {user} = useAuth();
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false)

  const handlePaystackPayment = async () => {
    if (!user){
      setIsLoginPromptOpen(true)
      return;
    }

    const amount = billingCycle === "yearly" ? "₦30,000/year" : "₦3,000/month";
    setLoading(true)

    try {
      let reponse = await initializeSubscription()

      const popup = new PaystackPop();
      popup.resumeTransaction(reponse.data.access_code, {
        onSuccess: async (transaction) => {
          console.log('Payment successful', transaction);
          toast.success("Payment successful! Thank you for subscribing.")

          if (transaction.status === "success") {
            // e.g. create the subscription in the backend
            try {
              await verifySubscription(transaction.reference);
              toast.success("Subscription verified successfully!")
            } catch (error) {
              let errorMessage = error.response?.data?.message || "An error occurred. Send a message to Customer Support.."
              console.error("Error initializing subscription:", error)
              toast.error(errorMessage)
              setLoading(false)
              return
            }
          }
        },
        onCancel: () => {
          console.log('Payment modal closed by user');
          toast.error("Payment cancelled. Please try again.")
          // handle user closing the payment modal
        },
        onError: (error) => {
          console.error('Error loading transaction', error);
        },
        onClose: () => {
          console.log('Payment modal closed');
          setLoading(false)
        }
      })
    } catch (error) {
      let errorMessage = error.response?.data?.message || "An error occurred. Please try again."
      console.error("Error initializing payment:", error)
      toast.error(errorMessage)
      setLoading(false)
      return
    } 
  }

  return (
    <div className="container mx-auto pt-8 pb-12 px-4">
      <BackLink 
        text="Back to Home" 
        redirectUrl='/' 
        className="btn-sm gap-2 mb-4" 
      />

      <div className="text-center max-w-2xl mx-auto mb-10">
        <h1 className="text-4xl font-bold mb-3">One Plan. All Access.</h1>
        <p className="text-lg">Everything you need to pass in one simple subscription</p>
      </div>

      {/* Billing toggle */}
      <div className="flex justify-center mb-10">
        <div className="bg-base-200 rounded-full p-1 inline-flex">
          <button
            className={`px-6 py-2 rounded-full ${billingCycle === "monthly" ? "bg-primary text-primary-content" : ""}`}
            onClick={() => setBillingCycle("monthly")}
          >
            Monthly
          </button>
          <button
            className={`px-6 py-2 rounded-full ${billingCycle === "yearly" ? "bg-primary text-primary-content" : ""}`}
            onClick={() => setBillingCycle("yearly")}
          >
            Yearly
            {billingCycle === "yearly" && (
              <span className="ml-2 bg-success text-success-content text-xs px-2 py-0.5 rounded-full">
                2 Months Free
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Premium Plan */}
      <div className="max-w-xl mx-auto">
        <div className="bg-base-100 rounded-xl p-8 shadow-md border border-primary relative">
          {/* Popular badge */}
          <div className="absolute top-0 right-0 bg-primary text-primary-content py-1 px-4 rounded-bl-none rounded-xl font-medium">
            All-Inclusive Access
          </div>

          <div className="mb-4">
            <h2 className="text-3xl font-bold">Premium Plan</h2>
          </div>

          <div className="mb-6">
            <span className="text-5xl font-bold">
              {billingCycle === "monthly" ? "₦3,000" : "₦30,000"}
            </span>
            <span className="text-base-content/70">
              {billingCycle === "monthly" ? " / month" : " / year"}
            </span>
          </div>

          <div className="divider my-6"></div>

          <ul className="space-y-4 text-base">
            <li className="flex items-center gap-3"><Check className="size-5 text-primary" />500+ Practice Tests</li>
            <li className="flex items-center gap-3"><Check className="size-5 text-primary" />Comprehensive Study Materials</li>
            <li className="flex items-center gap-3"><Check className="size-5 text-primary" />Personalized Learning Paths</li>
            <li className="flex items-center gap-3"><Check className="size-5 text-primary" />Performance Analytics</li>
            <li className="flex items-center gap-3"><Check className="size-5 text-primary" />Dedicated Coach Support</li>
            <li className="flex items-center gap-3"><Check className="size-5 text-primary" />Step-by-Step Guides</li>
          </ul>

          <Button isLoading={loading} className="btn btn-primary w-full mt-8" onClick={handlePaystackPayment}>
            {billingCycle === "monthly" ? "Subscribe for ₦3,000/month" : "Subscribe for ₦30,000/year"}
          </Button>
        </div>
      </div>

      <LoginPromptModal
        isOpen={isLoginPromptOpen}
        onClose={() => setIsLoginPromptOpen(false)}
        description="You need to be logged in to subscribe"
      />
    </div>
  )
}
